import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Plus, Download, Loader2, Sparkles, Filter, ChevronLeft, ChevronRight } from "lucide-react";
import { toPng } from "html-to-image";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import CausalLinkDialog from "@/components/hfacs/CausalLinkDialog";
import { DIAGRAM_LEVEL_KEYS, computeCausalLayout } from "@/lib/causalLayout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DIAGRAM_LEVELS = [
  { key: "Influences organisationnelles", label: "Influences organisationnelles", style: "border-violet-500/50 bg-violet-50" },
  { key: "Facteurs de supervision", label: "Facteurs de supervision", style: "border-green-500/50 bg-green-50" },
  { key: "Conditions préalables", label: "Conditions préalables", style: "border-orange-500/50 bg-orange-50" },
  { key: "Actes dangereux", label: "Actes dangereux", style: "border-blue-500/50 bg-blue-50" },
];


const NORMAL_LINK_COLOR = "#000000";
const MAIN_CHAIN_COLOR = "#DC2626";

function DiagramNodeContent({ f }) {
  return (
    <>
      <p className="text-[9px] text-foreground/70 mb-0.5">{f.FAMILLE}</p>
      <p className="text-[10px] font-medium leading-snug text-foreground/90">{f.FACTEUR}</p>
      {f.JUSTIFICATION && (
        <p data-testid={`diagram-node-justification-${f.ID_FACTEUR}`} className="text-xs leading-snug text-foreground mt-1.5 pt-1.5 border-t border-border/60">
          {f.JUSTIFICATION}
        </p>
      )}
    </>
  );
}

export default function CausalDiagram() {
  const { id } = useParams();
  const [analysis, setAnalysis] = useState(null);
  const [factors, setFactors] = useState([]);
  const [links, setLinks] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [linkPreset, setLinkPreset] = useState(null);
  const [pendingSource, setPendingSource] = useState(null);
  const [lines, setLines] = useState([]);
  const [svgSize, setSvgSize] = useState({ width: 0, height: 0 });
  const [exporting, setExporting] = useState(false);
  const [showOnlyMainChain, setShowOnlyMainChain] = useState(false);
  const [scrollState, setScrollState] = useState({ canLeft: false, canRight: false, overflowing: false });
  const containerRef = useRef(null);
  const nodeRefs = useRef({});

  const load = useCallback(async () => {
    const [a, f, l] = await Promise.all([
      axios.get(`${API}/hfacs/${id}`),
      axios.get(`${API}/hfacs/${id}/factors`),
      axios.get(`${API}/hfacs/${id}/causal-links`),
    ]);
    setAnalysis(a.data);
    setFactors(f.data);
    setLinks(l.data);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const recompute = useCallback(() => {
    if (!containerRef.current) return;
    const container = containerRef.current;
    const rect = container.getBoundingClientRect();
    const scrollLeft = container.scrollLeft || 0;
    const scrollTop = container.scrollTop || 0;
    let maxX = 0, maxY = 0;
    Object.values(nodeRefs.current).forEach((el) => {
      if (!el) return;
      const r = el.getBoundingClientRect();
      maxX = Math.max(maxX, r.right - rect.left + scrollLeft);
      maxY = Math.max(maxY, r.bottom - rect.top + scrollTop);
    });
    setSvgSize({ width: maxX, height: maxY });
    const newLines = links.map((l) => {
      const sourceEl = nodeRefs.current[l.ID_SOURCE];
      const targetEl = nodeRefs.current[l.ID_CIBLE === "INCIDENT" ? "INCIDENT" : l.ID_CIBLE];
      if (!sourceEl || !targetEl) return null;
      const sRect = sourceEl.getBoundingClientRect();
      const tRect = targetEl.getBoundingClientRect();
      return {
        id: l.ID_LIEN,
        statut: l.STATUT_LIEN,
        x1: sRect.right - rect.left + scrollLeft,
        y1: sRect.top + sRect.height / 2 - rect.top + scrollTop,
        x2: tRect.left - rect.left + scrollLeft,
        y2: tRect.top + tRect.height / 2 - rect.top + scrollTop,
      };
    }).filter(Boolean);
    setLines(newLines);
    setScrollState({
      canLeft: container.scrollLeft > 4,
      canRight: container.scrollLeft < container.scrollWidth - container.clientWidth - 4,
      overflowing: container.scrollWidth > container.clientWidth + 4,
    });
  }, [links]);

  useEffect(() => {
    const t = setTimeout(recompute, 100);
    const container = containerRef.current;
    window.addEventListener("resize", recompute);
    container?.addEventListener("scroll", recompute, { passive: true });
    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", recompute);
      container?.removeEventListener("scroll", recompute);
    };
  }, [recompute, factors]);

  const scrollDiagram = (dir) => {
    containerRef.current?.scrollBy({ left: dir * 320, behavior: "smooth" });
  };

  const createLink = async (payload) => {
    try {
      await axios.post(`${API}/hfacs/${id}/causal-links`, payload);
      toast.success("Lien causal créé (statut : Proposé).");
      setDialogOpen(false);
      setLinkPreset(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la création du lien.");
    }
  };

  const handleNodeClick = (nodeId) => {
    if (pendingSource === nodeId) {
      setPendingSource(null);
      return;
    }
    if (pendingSource === null) {
      if (nodeId === "INCIDENT") {
        toast.error("L'incident ne peut pas être un facteur source.");
        return;
      }
      setPendingSource(nodeId);
      return;
    }
    setLinkPreset({ source: pendingSource, target: nodeId });
    setPendingSource(null);
    setDialogOpen(true);
  };

  useEffect(() => {
    const onKeyDown = (e) => { if (e.key === "Escape") setPendingSource(null); };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const updateLinkStatus = async (linkId, statut) => {
    try {
      await axios.put(`${API}/hfacs/${id}/causal-links/${linkId}`, { statut_lien: statut });
      load();
    } catch (e) {
      toast.error("Erreur lors de la mise à jour.");
    }
  };

  const factorsByLevel = (level) => factors.filter((f) => f.NIVEAU === level);
  const layout = useMemo(() => computeCausalLayout(factors, links), [factors, links]);

  const exportPng = async () => {
    if (!containerRef.current) return;
    setExporting(true);
    const node = containerRef.current;
    const prevWidth = node.style.width;
    const prevMaxWidth = node.style.maxWidth;
    const prevOverflow = node.style.overflow;
    try {
      node.style.maxWidth = "none";
      node.style.width = `${node.scrollWidth}px`;
      node.style.overflow = "visible";
      recompute();
      await new Promise((r) => setTimeout(r, 150));
      const dataUrl = await toPng(node, { backgroundColor: "#F1F5F9", pixelRatio: 2, skipFonts: true });
      const link = document.createElement("a");
      link.download = `Diagramme_causal_${id}.png`;
      link.href = dataUrl;
      link.click();
      toast.success("Diagramme exporté en image.");
    } catch (e) {
      toast.error("Erreur lors de l'export du diagramme.");
    } finally {
      node.style.width = prevWidth;
      node.style.maxWidth = prevMaxWidth;
      node.style.overflow = prevOverflow;
      recompute();
      setExporting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border/60 px-6 py-4 flex items-center justify-between">
        <Link to={`/hfacs/${id}`} data-testid="diagram-back-link" className="flex items-center gap-2 text-sm text-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> {id}
        </Link>
        <h1 className="font-heading uppercase font-black text-lg">Analyse causale HFACS</h1>
        <div className="flex items-center gap-2">
          <Button onClick={exportPng} disabled={exporting} variant="outline" data-testid="diagram-export-png-button" className="gap-2 border-border">
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Exporter en image
          </Button>
          <Button onClick={() => { setLinkPreset(null); setDialogOpen(true); }} data-testid="diagram-create-link-button" className="bg-primary hover:bg-primary/90 text-primary-foreground gap-2">
            <Plus className="h-4 w-4" /> Créer un lien
          </Button>
        </div>
      </header>

      <div className="max-w-[1600px] mx-auto px-10 pt-4">
        {pendingSource ? (
          <p data-testid="diagram-pending-hint" className="text-xs font-mono uppercase text-primary bg-primary/10 border border-primary/30 rounded-sm px-3 py-2">
            Facteur source sélectionné — cliquez sur le facteur ou l'incident cible (ou recliquez pour annuler, Échap pour annuler)
          </p>
        ) : (
          <p className="text-xs font-mono uppercase text-foreground">
            Cliquez sur un facteur puis sur sa cible pour créer un lien causal directement dans le diagramme
          </p>
        )}
        {layout.mainChainLinkIds.size > 0 && (
          <div className="mt-2 flex items-center justify-between gap-3">
            <p data-testid="diagram-main-chain-legend" className="flex items-center gap-2 text-xs font-mono uppercase text-red-600">
              <Sparkles className="h-3.5 w-3.5" /> Chaîne principale (la plus longue / la plus validée) surlignée en gras ci-dessous
            </p>
            <Button
              size="sm"
              variant={showOnlyMainChain ? "default" : "outline"}
              onClick={() => setShowOnlyMainChain((v) => !v)}
              data-testid="diagram-toggle-main-chain-only"
              className={showOnlyMainChain ? "gap-2 bg-red-500 hover:bg-red-500/90 text-black" : "gap-2 border-red-500/40 text-red-600 hover:bg-red-500/10"}
            >
              <Filter className="h-3.5 w-3.5" /> {showOnlyMainChain ? "Afficher tout le diagramme" : "Ne montrer que la chaîne principale"}
            </Button>
          </div>
        )}
      </div>

      {scrollState.overflowing && (
        <p data-testid="diagram-swipe-hint" className="max-w-[1600px] mx-auto px-10 mt-2 text-xs font-mono uppercase text-foreground flex items-center gap-2">
          <ChevronLeft className="h-3.5 w-3.5" /><ChevronRight className="h-3.5 w-3.5 -ml-2" /> Faites glisser horizontalement (ou pincez pour zoomer) pour voir toutes les colonnes
        </p>
      )}

      {scrollState.canLeft && (
        <button
          onClick={() => scrollDiagram(-1)}
          data-testid="diagram-scroll-left-button"
          aria-label="Défiler vers la gauche"
          className="fixed left-3 top-1/2 -translate-y-1/2 z-30 h-11 w-11 rounded-full bg-card/90 backdrop-blur border border-border flex items-center justify-center text-foreground hover:border-primary/60 hover:text-primary transition-colors shadow-lg"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
      )}
      {scrollState.canRight && (
        <button
          onClick={() => scrollDiagram(1)}
          data-testid="diagram-scroll-right-button"
          aria-label="Défiler vers la droite"
          className="fixed right-3 top-1/2 -translate-y-1/2 z-30 h-11 w-11 rounded-full bg-card/90 backdrop-blur border border-border flex items-center justify-center text-foreground hover:border-primary/60 hover:text-primary transition-colors shadow-lg"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      )}

      <div ref={containerRef} className="relative px-10 py-10 max-w-[1600px] mx-auto overflow-x-auto">
        <svg className="absolute top-0 left-0 pointer-events-none" style={{ width: svgSize.width || "100%", height: svgSize.height || "100%" }}>
          <defs>
            <marker id="arrow" markerWidth="4.5" markerHeight="4.5" refX="3.5" refY="2.25" orient="auto">
              <path d="M0,0 L4.5,2.25 L0,4.5 Z" style={{ fill: NORMAL_LINK_COLOR }} />
            </marker>
            <marker id="arrow-main-chain" markerWidth="4.5" markerHeight="4.5" refX="3.5" refY="2.25" orient="auto">
              <path d="M0,0 L4.5,2.25 L0,4.5 Z" style={{ fill: MAIN_CHAIN_COLOR }} />
            </marker>
          </defs>
          {lines.filter((l) => !showOnlyMainChain || layout.mainChainLinkIds.has(l.id)).map((l) => {
            const isMainChain = layout.mainChainLinkIds.has(l.id);
            return (
              <path
                key={l.id}
                data-testid={isMainChain ? `diagram-link-main-chain-${l.id}` : undefined}
                d={`M ${l.x1} ${l.y1} C ${(l.x1 + l.x2) / 2} ${l.y1}, ${(l.x1 + l.x2) / 2} ${l.y2}, ${l.x2} ${l.y2}`}
                fill="none"
                strokeWidth={isMainChain ? 2 : 1}
                stroke={isMainChain ? MAIN_CHAIN_COLOR : NORMAL_LINK_COLOR}
                style={{ opacity: !isMainChain && l.statut === "Rejeté" ? 0.3 : 1 }}
                markerEnd={isMainChain ? "url(#arrow-main-chain)" : "url(#arrow)"}
              />
            );
          })}
        </svg>

        <div className="grid gap-x-6 gap-y-4 relative z-10 items-start" style={{ gridTemplateColumns: "repeat(5, minmax(230px, 1fr))" }}>
          {DIAGRAM_LEVELS.map((lvl, colIdx) => (
            <p key={lvl.key} className="font-mono text-xs uppercase text-foreground font-semibold" style={{ gridColumn: colIdx + 1, gridRow: 1 }}>{lvl.label}</p>
          ))}
          <p className="font-mono text-xs uppercase text-foreground font-semibold" style={{ gridColumn: 5, gridRow: 1 }}>Incident / Événement</p>

          {DIAGRAM_LEVELS.map((lvl, colIdx) => {
            const items = factorsByLevel(lvl.key);
            const linked = items.filter((f) => layout.linkedIds.has(f.ID_FACTEUR));
            const unlinked = items.filter((f) => !layout.linkedIds.has(f.ID_FACTEUR));
            return (
              <div key={lvl.key} style={{ display: "contents" }}>
                {linked.map((f) => (
                  <div
                    key={f.ID}
                    ref={(el) => (nodeRefs.current[f.ID_FACTEUR] = el)}
                    data-testid={`diagram-node-${f.ID_FACTEUR}`}
                    onClick={() => handleNodeClick(f.ID_FACTEUR)}
                    style={{ gridColumn: colIdx + 1, gridRow: layout.rowAssigned[f.ID_FACTEUR] + 2 }}
                    className={`bg-card border rounded-md p-3 cursor-pointer transition-all hover:border-primary/60 ${lvl.style} ${pendingSource === f.ID_FACTEUR ? "ring-2 ring-primary border-primary" : ""} ${showOnlyMainChain && !layout.mainChainNodeIds.has(f.ID_FACTEUR) ? "hidden" : ""}`}
                  >
                    <DiagramNodeContent f={f} />
                  </div>
                ))}
                {!showOnlyMainChain && unlinked.map((f, idx) => (
                  <div
                    key={f.ID}
                    ref={(el) => (nodeRefs.current[f.ID_FACTEUR] = el)}
                    data-testid={`diagram-node-${f.ID_FACTEUR}`}
                    onClick={() => handleNodeClick(f.ID_FACTEUR)}
                    style={{ gridColumn: colIdx + 1, gridRow: layout.maxRow + 3 + idx }}
                    className={`bg-card border rounded-md p-3 opacity-80 cursor-pointer transition-all hover:border-primary/60 hover:opacity-100 ${lvl.style} ${pendingSource === f.ID_FACTEUR ? "ring-2 ring-primary border-primary opacity-100" : ""}`}
                  >
                    <DiagramNodeContent f={f} />
                  </div>
                ))}
              </div>
            );
          })}

          <div
            ref={(el) => (nodeRefs.current["INCIDENT"] = el)}
            data-testid="diagram-node-incident"
            onClick={() => handleNodeClick("INCIDENT")}
            style={{ gridColumn: 5, gridRow: 2 }}
            className="bg-card border border-red-500/60 bg-red-50 rounded-md p-3 cursor-pointer transition-all hover:border-red-400"
          >
            <p className="text-[10px] font-medium text-foreground/90">{analysis?.TYPE_EVENEMENT}</p>
            <p data-testid="diagram-node-justification-INCIDENT" className="text-xs leading-snug text-foreground mt-1.5 pt-1.5 border-t border-border/60">{analysis?.CONSEQUENCE}</p>
          </div>
        </div>

        {factors.length === 0 && (
          <p className="text-sm text-foreground">Aucun facteur retenu/examiné pour l'instant. Ajoutez des facteurs dans l'onglet "Facteurs HFACS" avant de créer des liens.</p>
        )}
      </div>

      <div className="max-w-6xl mx-auto px-10 pb-10">
        <p className="font-mono text-xs uppercase text-foreground mb-3">Liens causaux</p>
        <div className="space-y-2" data-testid="causal-links-list">
          {links.map((l) => (
            <div key={l.ID_LIEN} data-testid={`causal-link-row-${l.ID_LIEN}`} className="flex items-center justify-between bg-card border border-border rounded-md px-3 py-2 text-xs">
              <span className="flex items-center gap-2">
                {l.ID_SOURCE} → {l.ID_CIBLE} <span className="text-foreground">({l.NATURE_LIEN})</span>
                {layout.mainChainLinkIds.has(l.ID_LIEN) && (
                  <span data-testid={`causal-link-main-chain-badge-${l.ID_LIEN}`} className="flex items-center gap-1 text-[10px] font-mono uppercase text-red-600 bg-red-500/10 border border-red-500/40 rounded-full px-2 py-0.5">
                    <Sparkles className="h-3 w-3" /> Chaîne principale
                  </span>
                )}
              </span>
              <select
                data-testid={`causal-link-status-select-${l.ID_LIEN}`}
                value={l.STATUT_LIEN}
                onChange={(e) => updateLinkStatus(l.ID_LIEN, e.target.value)}
                className="bg-secondary border border-border rounded-sm px-2 py-1 text-xs"
              >
                {["Proposé", "À vérifier", "Validé", "Rejeté"].map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          ))}
          {links.length === 0 && <p className="text-sm text-foreground">Aucun lien causal pour le moment.</p>}
        </div>
      </div>

      <CausalLinkDialog
        open={dialogOpen}
        onOpenChange={(v) => { setDialogOpen(v); if (!v) setLinkPreset(null); }}
        factors={factors}
        onSubmit={createLink}
        preset={linkPreset}
      />
    </div>
  );
}
